import { EllipsedText, Loading, MaterialIcon, VirtualizedComponent } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ListItem, ListItemLabel } from 'baseui/list';
import { CSSProperties, SyntheticEvent, memo, useRef } from 'react';
import { ListChildComponentProps } from 'react-window';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useHover } from 'usehooks-ts';
import { useStyles } from '../../hooks/useStyles';
import { convoyState, selectedConvoyIdState, showConvoyEvaluationsState } from '../../storage/convoys';
import { StyledTooltip } from '../ui/StyledTooltip';

function ConvoyItem({ data, index, style }: Readonly<ListChildComponentProps<VirtualizedComponent['data']>>) {
  const { theme, css, overrides } = useStyles();
  const [selectedConvoyId, setSelectedConvoyId] = useRecoilState(selectedConvoyIdState);
  const [showConvoyEvaluations, setShowConvoyEvaluations] = useRecoilState(showConvoyEvaluationsState);
  const convoyId = data.items[index];
  const convoy = useRecoilValue(convoyState(convoyId));
  const hoverRef = useRef(null);
  const isHover = useHover(hoverRef);

  const isSelected = selectedConvoyId === convoyId;

  const onSelectConvoy = () => {
    setShowConvoyEvaluations(false);
    if (selectedConvoyId === convoyId) setSelectedConvoyId(undefined);
    else setSelectedConvoyId(convoyId);
  };

  const onShowEvaluations = (event: SyntheticEvent) => {
    event.stopPropagation();
    setSelectedConvoyId(convoyId);
    setShowConvoyEvaluations(true);
  };

  return (
    <span
      style={style as CSSProperties}
      ref={hoverRef}
    >
      {data.isLoaded(index) ? (
        <ListItem
          endEnhancer={() =>
            isSelected && showConvoyEvaluations ? (
              <span className={css({ height: theme.sizing.scale950, width: theme.sizing.scale950 })} />
            ) : (
              <StyledTooltip content="Ver evaluaciones">
                <Button
                  shape="circle"
                  kind="secondary"
                  size="compact"
                  onClick={onShowEvaluations}
                >
                  <MaterialIcon
                    name="linear_scale"
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
            )
          }
          overrides={overrides.listItemOverrides({
            onClick: onSelectConvoy,
            isHover,
            isSelected,
          })}
        >
          <ListItemLabel description={convoy?.separationDistance ? `${convoy.separationDistance} m` : undefined}>
            <span className={css({ display: 'flex', alignItems: 'center', gap: theme.sizing.scale100 })}>
              <StyledTooltip
                content={convoy?.active ? 'Activo' : 'Inactivo'}
                showArrow={false}
              >
                <MaterialIcon
                  name={convoy?.active ? 'power_settings_new' : 'do_not_disturb_on'}
                  success={convoy?.active}
                  warn={!convoy?.active}
                  size="mini"
                />
              </StyledTooltip>
              <EllipsedText inline>{convoy?.name ?? '-'}</EllipsedText>
            </span>
          </ListItemLabel>
        </ListItem>
      ) : (
        <Loading size="compact" />
      )}
    </span>
  );
}

export default memo(ConvoyItem);
